import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, MessageCircle, Wrench, HelpCircle, GraduationCap, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/seo/SEOHead';
import { distributor } from '../data/mock';
import { trustSignals } from '../data/approvedContent';
import { useMotion } from '../context/MotionContext';
import { trackCTAClick } from '../utils/analytics';
import GlitchText from '../components/ui/GlitchText';

const supportPathways = [
  {
    id: 'product-advice',
    icon: MessageCircle,
    title: 'Product Advice',
    description: 'Expert guidance on selecting the right XION products for your clinical needs.',
    features: [
      'System configuration recommendations',
      'Compatibility assessments',
      'Upgrade path planning',
      'Budget optimization'
    ]
  },
  {
    id: 'technical-support',
    icon: HelpCircle,
    title: 'Technical Support',
    description: 'Professional technical assistance for XION equipment operation and troubleshooting.',
    features: [
      'Remote diagnostics',
      'Software updates',
      'User training',
      'Documentation support'
    ]
  },
  {
    id: 'repair',
    icon: Wrench,
    title: 'Repair Services',
    description: 'Certified repair and maintenance services using genuine XION parts.',
    features: [
      'Factory-certified technicians',
      'Genuine replacement parts',
      'Calibration services',
      'Preventive maintenance'
    ]
  }
];

const additionalServices = [
  {
    icon: GraduationCap,
    title: 'Training Programs',
    description: 'Comprehensive training for medical staff on XION equipment operation and best practices.'
  },
  {
    icon: Shield,
    title: 'Warranty Support',
    description: 'Full warranty coverage and extended warranty options for all XION products.'
  }
];

const ServiceSupportPage = () => {
  const { prefersReducedMotion } = useMotion();

  const handleFMSClick = () => {
    trackCTAClick('fms', 'ServiceSupportPage');
  };

  return (
    <>
      <SEOHead
        title="Service & Support"
        description="XION Medical service and support in the Philippines. Product advice, technical support, and repair services through FMSInc., our exclusive distributor."
        path="/service-support"
      />
      <Header />
      <main className="pt-32 min-h-screen bg-black grid-bg">
        <div className="max-w-7xl mx-auto px-6 py-16">
          {/* Page Header */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <p className="terminal-label text-[#00FFD1] mb-4">// Customer Care</p>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {prefersReducedMotion ? 'Service & Support' : <GlitchText text="Service & Support" />}
            </h1>
            <p className="text-white/60 text-lg max-w-3xl">
              Comprehensive support services backed by German engineering excellence and 
              local expertise through our Philippine distributor.
            </p>
          </motion.div>

          {/* PH Support Notice */}
          {/* // Distributor exclusivity statement is client-provided; confirm final legal wording before launch. */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#00FFD1]/5 border border-[#00FFD1]/30 p-6 mb-12"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-white font-semibold text-lg mb-1">Philippines Service & Support</h3>
                <p className="text-white/60 text-sm">
                  For Philippine inquiries, all support is provided through our exclusive distributor:
                  <span className="text-[#00FFD1] font-medium ml-1">{distributor.name}</span>
                </p>
              </div>
              <a
                href={distributor.website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleFMSClick}
                className="btn-primary text-sm whitespace-nowrap"
              >
                Contact FMSInc.
                <ExternalLink size={14} />
              </a>
            </div>
          </motion.div>

          {/* Support Pathways */}
          <section className="mb-16">
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="mb-8"
            >
              <h2 className="text-3xl font-bold text-white mb-2">Support Pathways</h2>
              <p className="text-white/60">Choose the support channel that best fits your needs.</p>
            </motion.div>

            <div className="grid lg:grid-cols-3 gap-6">
              {supportPathways.map((pathway, index) => {
                const Icon = pathway.icon;
                return (
                  <motion.div
                    key={pathway.id}
                    initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="card-dark p-6"
                  >
                    <div className="w-14 h-14 border border-[#00FFD1]/30 flex items-center justify-center mb-4">
                      <Icon className="text-[#00FFD1]" size={28} />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">{pathway.title}</h3>
                    <p className="text-white/60 text-sm mb-4">{pathway.description}</p>
                    <ul className="space-y-2 mb-6">
                      {pathway.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-white/50 text-sm">
                          <span className="w-1 h-1 bg-[#00FFD1] rounded-full" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact-ph"
                      className="text-[#00FFD1] text-sm font-mono hover:underline flex items-center gap-1"
                    >
                      Request Support (PH) <ArrowRight size={14} />
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* Additional Services */}
          <section className="mb-16">
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="mb-8"
            >
              <h2 className="text-3xl font-bold text-white mb-2">Additional Services</h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {additionalServices.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={index}
                    initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="card-dark p-6 flex items-start gap-4"
                  >
                    <div className="w-12 h-12 border border-[#00FFD1]/30 flex items-center justify-center shrink-0">
                      <Icon className="text-[#00FFD1]" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                      <p className="text-white/60 text-sm">{service.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* Quality Assurance */}
          <motion.section
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#0a0a0a] border border-white/10 p-8 md:p-12 mb-12"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="terminal-label text-[#00FFD1] mb-3">// Quality Assurance</p>
                <h3 className="text-2xl font-bold text-white mb-4">
                  {trustSignals.madeInGermany}
                </h3>
                <p className="text-white/60 mb-4">
                  {trustSignals.qualityStandards}. Our service network ensures your XION equipment 
                  maintains peak performance throughout its lifecycle.
                </p>
                <ul className="space-y-2 text-white/50 text-sm">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#00FFD1] rounded-full" />
                    Factory-authorized service centers
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#00FFD1] rounded-full" />
                    Genuine XION replacement parts
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#00FFD1] rounded-full" />
                    Certified technician network
                  </li>
                </ul>
              </div>
              <div className="flex flex-col gap-4">
                <Link to="/contact-ph" className="btn-primary w-full justify-center">
                  Request Service (PH)
                  <ArrowRight size={16} />
                </Link>
                <a
                  href={distributor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleFMSClick}
                  className="btn-secondary w-full justify-center"
                >
                  Contact FMSInc.
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </motion.section>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ServiceSupportPage;
